import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

import { cn } from "@shared/lib/utils";

import { useEcosystemCategories, type EcosystemCategory } from "./featureCategories";

function CategoryCard({ category }: { category: EcosystemCategory }) {
  const { t } = useTranslation("landing");
  const CategoryIcon = category.icon;

  return (
    <article
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-3xl border border-border/60 bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl sm:p-7",
        category.span === 2 && "lg:col-span-2",
      )}
    >
      <div
        className="pointer-events-none absolute -end-12 -top-12 h-40 w-40 rounded-full bg-primary/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        aria-hidden="true"
      />

      <header className="relative flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
            <CategoryIcon className="h-6 w-6" aria-hidden="true" />
          </span>
          <div>
            <h3 className="font-serif text-2xl font-semibold tracking-tight text-foreground">
              {t(`ecosystem.categories.${category.key}.title`)}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t(`ecosystem.categories.${category.key}.subtitle`)}
            </p>
          </div>
        </div>
        <span className="hidden shrink-0 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground sm:inline-flex">
          {t("ecosystem.categoryCount", { count: category.features.length })}
        </span>
      </header>

      <ul
        className={cn(
          "relative mt-6 grid gap-2.5",
          category.span === 2 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2",
        )}
      >
        {category.features.map((feature) => {
          const FeatureIcon = feature.icon;
          return (
            <li
              key={feature.key}
              className="flex items-center gap-2.5 rounded-2xl border border-border/40 bg-background/60 px-3 py-2.5 text-sm text-foreground/90 transition-colors duration-200 group-hover:border-border"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                <FeatureIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <span className="truncate">
                {t(`ecosystem.categories.${category.key}.features.${feature.key}`)}
              </span>
            </li>
          );
        })}
      </ul>
    </article>
  );
}

export function EcosystemSection() {
  // Only features enabled on this deployment are advertised.
  const { categories, featureCount, categoryCount } = useEcosystemCategories();
  const { t } = useTranslation("landing");

  return (
    <section
      id="ecosystem"
      className="relative overflow-hidden bg-background py-20 sm:py-24 lg:py-28"
      aria-labelledby="ecosystem-heading"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 bg-gradient-to-b from-muted/40 to-transparent" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-primary shadow-sm">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            {t("ecosystem.eyebrow")}
          </span>
          <h2
            id="ecosystem-heading"
            className="mt-5 font-serif text-4xl font-semibold leading-[1.1] tracking-[-0.04em] text-foreground sm:text-5xl lg:text-6xl"
          >
            {t("ecosystem.heading")}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
            {t("ecosystem.subheading")}
          </p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-medium text-primary">
            <span className="flex h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
            {t("ecosystem.counterChip", {
              features: featureCount,
              categories: categoryCount,
            })}
          </p>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-7">
          {categories.map((category) => (
            <CategoryCard key={category.key} category={category} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default EcosystemSection;
